import { ReactElement, useEffect, Suspense } from 'react';
import {
  useRoutes,
  Navigate,
  RouteObject,
  Outlet,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useUser } from 'utils/auth';
import { AdminPinModal } from 'components/common/Modal/variants/AdminPinModal';
import { Welcome } from 'pages/welcome/Welcome';
import { IndexRoutes, ProtectedRoutes, PublicRoutes } from 'types/Routes';
import { NotFound } from 'pages/landing/NotFound';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { lazyImport } from 'utils/lazyimport';
import { Login } from 'pages/auth/Login';
import { Registration } from 'pages/auth/Registration';
import { ForgotPassword } from 'pages/auth/ForgotPassword';
import { ResetPassword } from 'pages/auth/ResetPassword';
import { Article } from 'pages/in-app/knowledge/Article';
import { Landing } from 'pages/landing/Landing';
import { useGlobalLinkInteractions } from 'hooks/useGlobalLinkInteractions';
import { SessionStorageKey, useSessionStorage } from 'hooks/useSessionStorage';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { toastContent } from 'config/toast';
import { SuccessStatusType } from 'types/StatusTypes';
import { UserDashboard } from 'pages/in-app/organization-management/UserDashboard';
import { VoucherDashboard } from 'pages/in-app/organization-management/VoucherDashboard';
import { ClassDashboard } from 'pages/in-app/organization-management/ClassDashboard';
import { ClassDetail } from 'pages/in-app/organization-management/ClassDetail';
import { WorksheetDashboard } from 'pages/in-app/worksheet/WorksheetDashboard';
import { Worksheet } from 'pages/in-app/worksheet/Worksheet';
import { WorksheetSession } from 'pages/in-app/worksheet/WorksheetSession';
import { WorksheetSharePage } from 'pages/in-app/worksheet/WorksheetSharePage';
import { WorksheetUserAnswersDashboard } from 'pages/in-app/worksheet/WorksheetUserAnswersDashboard';
import { WorksheetUserAnswerReview } from 'pages/in-app/worksheet/WorksheetUserAnswerReview';
import { ComponentPreview } from 'pages/in-app/styleguide/ComponentPreview';
import { ProtectedRoute } from 'components/common/ProtectedRoute';
import { SchoolUserRole } from '@novaheal/types';
import { ENV } from 'config/env';
import { WorksheetClassDetail } from 'pages/in-app/worksheet/WorksheetClassDetail';
import { WorksheetTemplatePreview } from 'pages/in-app/worksheet/WorksheetTemplatePreview';
import { useAdminImpersonationActivation } from 'hooks/useAdminImpersonationActivation';
import { useEducationProgressionPromptModal } from 'hooks/education';

// Lazy Imports

const { Library } = lazyImport(() => import('../pages/in-app/knowledge/Library'), 'Library');
const { Home } = lazyImport(() => import('../pages/in-app/home/Home'), 'Home');
const { CuratedList } = lazyImport(() => import('../pages/in-app/home/CuratedList'), 'CuratedList');
const { Category } = lazyImport(() => import('../pages/in-app/knowledge/Category'), 'Category');
const { Favorites } = lazyImport(() => import('../pages/in-app/Favorites'), 'Favorites');
const { SearchResults } = lazyImport(
  () => import('../pages/in-app/SearchResults'),
  'SearchResults'
);
const { Settings } = lazyImport(() => import('../pages/in-app/settings/Settings'), 'Settings');
const { Profile } = lazyImport(() => import('../pages/in-app/settings/Profile'), 'Profile');
const { Voucher } = lazyImport(() => import('../pages/in-app/settings/Voucher'), 'Voucher');
const { Referral } = lazyImport(() => import('../pages/in-app/settings/Referral'), 'Referral');
const { Legal } = lazyImport(() => import('../pages/in-app/settings/Legal'), 'Legal');
const { Support } = lazyImport(() => import('../pages/in-app/settings/Support'), 'Support');
const { Folder } = lazyImport(() => import('../pages/in-app/knowledge/Folder'), 'Folder');
const { ChangePassword } = lazyImport(
  () => import('../pages/in-app/settings/ChangePassword'),
  'ChangePassword'
);
const { Medication } = lazyImport(
  () => import('../pages/in-app/knowledge/Medication'),
  'Medication'
);
const { PaymentRedirect } = lazyImport(
  () => import('../pages/in-app/subscription/PaymentRedirect'),
  'PaymentRedirect'
);
const { NewsOverviewPage } = lazyImport(
  () => import('../pages/in-app/home/NewsOverviewPage'),
  'NewsOverviewPage'
);
const { NewsPage } = lazyImport(() => import('../pages/in-app/home/NewsPage'), 'NewsPage');
const { PremiumManagement } = lazyImport(
  () => import('../pages/in-app/subscription/PremiumManagement'),
  'PremiumManagement'
);
const { Survey } = lazyImport(() => import('../pages/in-app/settings/Survey'), 'Survey');

const { AiSubscriptionManagement } = lazyImport(
  () => import('../pages/in-app/settings/AiSubscriptionManagement'),
  'AiSubscriptionManagement'
);

const { AiSubscriptionRedirect } = lazyImport(
  () => import('../pages/in-app/settings/AiSubscriptionRedirect'),
  'AiSubscriptionRedirect'
);

const { DeleteAccount } = lazyImport(
  () => import('../pages/in-app/settings/DeleteAccount'),
  'DeleteAccount'
);

const { CancelSubscription } = lazyImport(
  () => import('../pages/in-app/settings/CancelSubscription'),
  'CancelSubscription'
);

let showedToast = false;

export const AppRoutes = (): ReactElement | null => {
  const { handleLinkInteractions } = useGlobalLinkInteractions();
  const { data: user } = useUser();
  const voucherShortCodeStorage = useSessionStorage<string>(SessionStorageKey.VOUCHER_SHORT_CODE);

  const [params, setSearchParams] = useSearchParams();
  const toast = useNovaToast();
  const { pendingAdminToken, handlePinSuccess, handlePinClose } =
    useAdminImpersonationActivation();
  useEffect(() => {
    if (params.get('emailVerified') === 'true' && !showedToast) {
      toast.show(toastContent.success[SuccessStatusType.EMAIL_VERIFICATION]);
      showedToast = true;
    }
    if (params.get('shortCode')) {
      const shortCode = params.get('shortCode');
      if (shortCode) {
        voucherShortCodeStorage.setValue(shortCode);
      }
    }
    const cmId = params.get('cmId');
    if (cmId) {
      sessionStorage.setItem(SessionStorageKey.CASEME_NEWSLETTER_AWAITING, cmId);
    }
  }, [toast, params, voucherShortCodeStorage]);

  useEffect(() => {
    if (user && !user.isFirstTimeUse) {
      handleLinkInteractions();
    }
  }, [user, handleLinkInteractions]);

  const routes: RouteObject[] = [
    {
      path: PublicRoutes.BASE,
      element: <PublicRouteGuard />,
      children: [
        { path: PublicRoutes.LOGIN, element: <Login /> },
        { path: PublicRoutes.REGISTER, element: <Registration /> },
        { path: PublicRoutes.FORGOT_PASSWORD, element: <ForgotPassword /> },
        { path: PublicRoutes.RESET_PASSWORD, element: <ResetPassword /> },
        { path: PublicRoutes.BASE, element: <Landing /> },
        { path: '*', element: <NotFound /> },
      ],
    },
    {
      path: IndexRoutes.BASE,
      element: <IndexRouteGuard />,
      children: [{ path: IndexRoutes.FIRST_TIME, element: <Welcome /> }],
    },
    {
      path: ProtectedRoutes.BASE,
      element: (
        <Suspense fallback={<PageSpinner />}>
          <PrivateRouteGuard />
        </Suspense>
      ),
      children: [
        {
          path: ProtectedRoutes.NOT_FOUND_FALLBACK,
          element: (
            <NotFound buttonText="Zurück zur Startseite" buttonLink={ProtectedRoutes.BASE} />
          ),
        },
        { path: ProtectedRoutes.HOME, element: <Home /> },
        { path: `${ProtectedRoutes.CURATED_LIST}/:id`, element: <CuratedList /> },
        { path: ProtectedRoutes.NEWS, element: <NewsOverviewPage /> },
        { path: `${ProtectedRoutes.NEWS}/:id`, element: <NewsPage /> },
        { path: ProtectedRoutes.KNOWLEDGE, element: <Library /> },
        { path: ProtectedRoutes.SEARCH, element: <SearchResults /> },
        {
          path: `${ProtectedRoutes.ARTICLE}/:id`,
          element: (
            <ProtectedRoute requirePremiumAccess requireEmailVerified>
              <Article />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ProtectedRoutes.MEDICATION}/:id`,
          element: (
            <ProtectedRoute requirePremiumAccess requireEmailVerified>
              <Medication />
            </ProtectedRoute>
          ),
        },
        { path: `${ProtectedRoutes.FOLDER}/:id`, element: <Folder /> },
        { path: `${ProtectedRoutes.CATEGORY}/:id`, element: <Category /> },
        { path: ProtectedRoutes.FAVORITES, element: <Favorites /> },
        { path: ProtectedRoutes.SETTINGS, element: <Settings /> },
        { path: ProtectedRoutes.PROFILE, element: <Profile /> },
        { path: ProtectedRoutes.PASSWORD, element: <ChangePassword /> },
        { path: ProtectedRoutes.VOUCHER, element: <Voucher /> },
        { path: ProtectedRoutes.PAYMENT_MANAGEMENT, element: <PremiumManagement /> },
        { path: ProtectedRoutes.PAYMENT_REDIRECT, element: <PaymentRedirect /> },
        { path: ProtectedRoutes.LEGAL, element: <Legal /> },
        { path: ProtectedRoutes.SUPPORT, element: <Support /> },
        { path: ProtectedRoutes.SURVEY, element: <Survey /> },
        { path: ProtectedRoutes.DELETE_ACCOUNT, element: <DeleteAccount /> },
        {
          path: ProtectedRoutes.CANCEL_SUBSCRIPTION,
          element: <CancelSubscription />,
        },
        { path: ProtectedRoutes.REFERRAL, element: <Referral /> },
        {
          path: ProtectedRoutes.AI_SUBSCRIPTION_MANAGEMENT,
          element: (
            <ProtectedRoute>
              <AiSubscriptionManagement />
            </ProtectedRoute>
          ),
        },
        {
          path: ProtectedRoutes.AI_SUBSCRIPTION_REDIRECT,
          element: (
            <ProtectedRoute>
              <AiSubscriptionRedirect />
            </ProtectedRoute>
          ),
        },
        {
          path: ProtectedRoutes.ORG_MANAGEMENT_USER_DASHBOARD,
          element: (
            <ProtectedRoute requireSchoolRoles={[SchoolUserRole.ADMIN]}>
              <UserDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: ProtectedRoutes.ORG_MANAGEMENT_VOUCHER_DASHBOARD,
          element: (
            <ProtectedRoute requireSchoolRoles={[SchoolUserRole.ADMIN]}>
              <VoucherDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: ProtectedRoutes.ORG_MANAGEMENT_CLASS_DASHBOARD,
          element: (
            <ProtectedRoute requireSchoolRoles={[SchoolUserRole.ADMIN]}>
              <ClassDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ProtectedRoutes.ORG_MANAGEMENT_CLASS_DETAIL}/:id`,
          element: (
            <ProtectedRoute
              requireSchoolRoles={[
                SchoolUserRole.ADMIN,
                SchoolUserRole.TEACHER,
                SchoolUserRole.GUIDE,
              ]}
            >
              <ClassDetail />
            </ProtectedRoute>
          ),
        },
        {
          path: ProtectedRoutes.WORKSHEET_DASHBOARD,
          element: (
            <ProtectedRoute requireTeacherOrAdmin>
              <WorksheetDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ProtectedRoutes.WORKSHEET_TEMPLATE_PREVIEW}/:id`,
          element: (
            <ProtectedRoute requireTeacherOrAdmin>
              <WorksheetTemplatePreview />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ProtectedRoutes.WORKSHEET_DETAIL}/:id`,
          element: (
            <ProtectedRoute requireTeacherOrAdmin>
              <Worksheet />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ProtectedRoutes.WORKSHEET_CLASS_DETAIL}/:id`,
          element: (
            <ProtectedRoute
              requireSchoolRoles={[
                SchoolUserRole.ADMIN,
                SchoolUserRole.TEACHER,
                SchoolUserRole.GUIDE,
              ]}
            >
              <WorksheetClassDetail />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ProtectedRoutes.WORKSHEET_SESSION}/:id`,
          element: <WorksheetSession />,
        },
        {
          path: `${ProtectedRoutes.WORKSHEET_SHARE}/:token`,
          element: (
            <ProtectedRoute requireTeacherOrAdmin>
              <WorksheetSharePage />
            </ProtectedRoute>
          ),
        },
        {
          path: `${ProtectedRoutes.WORKSHEET_USER_ANSWERS_DASHBOARD}`,
          element: <WorksheetUserAnswersDashboard />,
        },
        {
          path: `${ProtectedRoutes.WORKSHEET_USER_ANSWERS_REVIEW}/:id`,
          element: <WorksheetUserAnswerReview />,
        },
        ...(ENV !== 'production'
          ? [
              {
                path: ProtectedRoutes.STYLEGUIDE,
                element: <ComponentPreview />,
              },
            ]
          : []),
      ],
    },
  ];

  const element = useRoutes(routes);

  return (
    <>
      {element}
      {pendingAdminToken && (
        <AdminPinModal
          isOpen
          adminToken={pendingAdminToken}
          onSuccess={handlePinSuccess}
          onClose={handlePinClose}
        />
      )}
    </>
  );
};

const PrivateRouteGuard = (): ReactElement | null => {
  const { data: user } = useUser();
  const location = useLocation();
  const { handleLinkInteractions, handleLinkExtractionInteraction } = useGlobalLinkInteractions();
  const deferredUrlStorage = useSessionStorage<string>(SessionStorageKey.DEFERRED_URL);
  const didLogoutStorage = useSessionStorage<boolean>(SessionStorageKey.DID_USER_LOGOUT);

  useEducationProgressionPromptModal({
    enabled: !!user && !user.isFirstTimeUse,
  });

  if (!user) {
    deferredUrlStorage.setValue(window.location.pathname + window.location.search);
    handleLinkExtractionInteraction();
    return <Navigate to={PublicRoutes.BASE} />;
  }

  if (user.isFirstTimeUse) {
    return <Navigate to={IndexRoutes.FIRST_TIME} />;
  }

  const didUserLogout = !!didLogoutStorage.getValue();
  const deferredUrl = deferredUrlStorage.getValue();

  const isRedirectToSettingAfterLogout =
    didUserLogout && deferredUrl && deferredUrl.includes(ProtectedRoutes.SETTINGS);

  if (deferredUrl && deferredUrl !== location.pathname && !isRedirectToSettingAfterLogout) {
    deferredUrlStorage.deleteItem();
    handleLinkInteractions();
    return <Navigate to={deferredUrl} />;
  }

  didLogoutStorage.deleteItem();
  deferredUrlStorage.deleteItem();
  return <Outlet />;
};

const PublicRouteGuard = (): ReactElement => {
  const { data: user } = useUser();
  const deferredUrlStorage = useSessionStorage<string>(SessionStorageKey.DEFERRED_URL);
  const didLogoutStorage = useSessionStorage<boolean>(SessionStorageKey.DID_USER_LOGOUT);

  if (user) {
    const didUserLogout = !!didLogoutStorage.getValue();
    const deferredUrl = deferredUrlStorage.getValue();

    const isRedirectToSettingAfterLogout =
      didUserLogout && deferredUrl && deferredUrl.includes(ProtectedRoutes.SETTINGS);

    return (
      <Navigate
        to={!isRedirectToSettingAfterLogout && deferredUrl ? deferredUrl : ProtectedRoutes.BASE}
      />
    );
  }

  return <Outlet />;
};

const IndexRouteGuard = (): ReactElement | null => {
  const { data: user, isFetched } = useUser();
  const didLogoutStorage = useSessionStorage<boolean>(SessionStorageKey.DID_USER_LOGOUT);
  const deferredUrlStorage = useSessionStorage<string>(SessionStorageKey.DEFERRED_URL);

  // prevent screen flashing when user data is not fetched yet
  if (!isFetched) {
    return null;
  }

  if (user && !user.isFirstTimeUse) {
    const deferredUrl = deferredUrlStorage.getValue();
    const didUserLogout = !!didLogoutStorage.getValue();
    const isRedirectToSettingAfterLogout =
      didUserLogout && deferredUrl && deferredUrl.includes(ProtectedRoutes.SETTINGS);

    return (
      <Navigate
        to={!isRedirectToSettingAfterLogout && deferredUrl ? deferredUrl : ProtectedRoutes.BASE}
      />
    );
  }

  return <Outlet />;
};
